import {bookingWindowValues, yearInDays} from "@hosttools/core/constant";
import {
    getBookingWindowText,
    getCalendarExportCode,
    isListingPricingPro
} from "@hosttools/core/shared/utils/utils.listing";
import {
    ButtonIconTooltip,
    FormField,
    FormGroup,
    HStack,
    Input,
    InputField,
    InputRightAddon,
    Link,
    Pressable,
    ProFeatureLocked,
    RefreshIcon,
    SelectGeneric,
    Switch,
    Text,
    useFormContext,
    useListingService,
    useNumberInputRules,
    useUserContext,
    VStack
} from "@hosttools/frontend";
import type {Listing, ListingChannel} from "@hosttools/frontend/models/listing";
import {type Lock} from "@hosttools/frontend/models/lock";
import {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState
} from "react";
import type {SubmitHandler} from "react-hook-form";
import {Controller, useForm, useWatch} from "react-hook-form";

import ImportCalendars from "@/admin/components/ModalListingSetting/ImportCalendars";
import SelectListings from "@/admin/components/ModalListingSetting/SelectListings";
import SmartLock from "@/admin/components/ModalListingSetting/SmartLock";
import type {PriceSourceOption} from "@/admin/components/SelectPriceSource";
import SelectPriceSource from "@/admin/components/SelectPriceSource";
import CopyText from "@/client/components/CopyText";
import {generateHex} from "@/client/utils/generateHexCode";

type ListingSettingsFormType = Partial<Listing> & {
    checkedMessageOnly?: boolean;
    linkedRoomIDs?: string[];
    linkedLockIDs?: string[];
};

type Props = {
    listing: Listing;
    channels: ListingChannel[];
    onSubmitSuccess?: (listingID: string) => void;
    onEditListingName?: () => void;
};

type ListingSettingsRef = {
    submit: (params: {onError: (reason: "not_dirty" | "has_errors") => void}) => Promise<void>;
};

const convertListingToDefaultValues = (listing: Listing): ListingSettingsFormType => {
    const newListingSettings: ListingSettingsFormType = {
        ...listing,
        calendarExportCode: listing.calendarExportCode ?? "",
        hidden: listing.hidden && !listing.listingEnabled // If listing is enabled then it shouldn't be hidden
    };
    if (!listing.priceSource) {
        newListingSettings.priceSource = "Amount";
    }
    // If the listing has pricing disabled but availability enabled, then the price source should be Beyond Pricing
    if (!listing.pricingEnabled && listing.syncAvailability) {
        newListingSettings.priceSource = "Beyond Pricing";
    }
    if (!listing.basePrice) {
        newListingSettings.basePrice = 0;
    }
    if (!Number.isNaN(Number(listing.minNights))) {
        newListingSettings.minNights = listing.minNights;
    } else {
        newListingSettings.minNights = 1;
    }
    if (!listing.bookingWindow && listing.bookingWindow !== 0) {
        newListingSettings.bookingWindow = yearInDays;
    }
    newListingSettings.checkedMessageOnly =
        !newListingSettings.pricingEnabled && !newListingSettings.syncAvailability;

    return newListingSettings;
};

const ListingSettings = forwardRef<ListingSettingsRef, Props>(
    ({listing, channels, onSubmitSuccess, onEditListingName}, ref) => {
        const {
            listings,
            accounts,
            locks,
            user: {canEditLock}
        } = useUserContext();
        const formContext = useFormContext<ListingSettingsFormType>();
        const listingService = useListingService();

        const {
            control,
            formState: {errors, isDirty, isValid, submitCount},
            register,
            setValue,
            getValues,
            handleSubmit,
            reset
        } = useForm<ListingSettingsFormType>({
            defaultValues: convertListingToDefaultValues(listing)
        });
        const [isMessageOnlyScope, setIsMessageOnlyScope] = useState(false);
        const discountNumberRules = useNumberInputRules({min: 0, max: 100});
        const isPro = useMemo(() => isListingPricingPro(listing), [listing]);
        const calendarExportCode = useWatch({control, name: "calendarExportCode"});
        const calendarExportURL = getCalendarExportCode({calendarExportCode}, __IS_BETA__);

        const hidden = useWatch({
            control,
            name: "hidden"
        });

        const checkedMessageOnly = useWatch({
            control,
            name: "checkedMessageOnly"
        });

        const priceSource = useWatch({
            control,
            name: "priceSource"
        });

        const listingEnabled = useWatch({
            control,
            name: "listingEnabled"
        });

        const bookingWindowOptions = useMemo(
            () =>
                bookingWindowValues.map(bookingWindow => {
                    return {
                        label: getBookingWindowText({bookingWindow}),
                        value: `${bookingWindow}`
                    };
                }),
            []
        );

        const handleSave: SubmitHandler<ListingSettingsFormType> = useCallback(
            async listingSettings => {
                const {_id} = listing;
                const {linkedRoomIDs} = listingSettings;
                const {linkedLockIDs} = listingSettings;

                if (listingSettings.checkedMessageOnly) {
                    listingSettings.priceSource = listing.priceSource;
                    listingSettings.minNights = listing.minNights;
                    listingSettings.minPrice = listing.minPrice;
                    listingSettings.basePrice = listing.basePrice;
                }
                if (listingSettings.priceSource === "Beyond Pricing") {
                    listingSettings.minNights = listing.minNights;
                    listingSettings.minPrice = listing.minPrice;
                    listingSettings.basePrice = listing.basePrice;
                    listingSettings.pricingEnabled = false;
                }

                await listingService.post("/editListingSettings", {
                    linkedRoomIDs,
                    linkedLockIDs,
                    ...listingSettings,
                    _id
                });
                onSubmitSuccess?.(listing._id);
            },
            [listing, listingService, onSubmitSuccess]
        );

        const generateCalendarExportCode = useCallback(async () => {
            setValue("calendarExportCode", generateHex(), {
                shouldDirty: true
            });
        }, [setValue]);

        const handleChangeLinkedRooms = useCallback(
            (selectedListing: Listing[]) => {
                setValue(
                    "linkedRoomIDs",
                    selectedListing.map(listing => listing._id)
                );
            },
            [setValue]
        );

        const handleChangePriceSource = useCallback(
            (option: PriceSourceOption | null) => {
                if (option) {
                    setValue("priceSource", option.value, {shouldDirty: true});
                }
            },
            [setValue]
        );

        const handleChangeLinkedLocks = useCallback(
            (selectedLocks: Lock[]) => {
                setValue(
                    "linkedLockIDs",
                    selectedLocks.map(lock => lock._id)
                );
            },
            [setValue]
        );

        const handleChangeListingEnabled = useCallback(
            (checked: boolean) => {
                setValue("listingEnabled", checked, {shouldDirty: true});
                if (checked) {
                    setValue("hidden", false);
                }
            },
            [setValue]
        );

        const handleToggleMessageOnly = useCallback(
            (isChecked: boolean) => {
                setValue("checkedMessageOnly", isChecked, {shouldDirty: true});
                setValue("pricingEnabled", !isChecked && priceSource !== "Beyond Pricing");
                setValue("syncAvailability", !isChecked);
            },
            [priceSource, setValue]
        );

        const handleAddSmartLock = useCallback(() => {
            const value = getValues("linkedLockIDs");
            setValue("linkedLockIDs", value, {shouldDirty: true});
        }, [getValues, setValue]);

        const handleRemoveSmartLock = useCallback(() => {
            const value = getValues("linkedLockIDs");
            setValue("linkedLockIDs", value, {shouldDirty: true});
        }, [getValues, setValue]);

        const handleAddLinkedRoom = useCallback(() => {
            const value = getValues("linkedRoomIDs");
            setValue("linkedRoomIDs", value, {shouldDirty: true});
        }, [getValues, setValue]);

        const handleRemoveLinkedRoom = useCallback(() => {
            const value = getValues("linkedRoomIDs");
            setValue("linkedRoomIDs", value, {shouldDirty: true});
        }, [getValues, setValue]);

        useImperativeHandle(
            ref,
            () => ({
                submit: async ({onError}) => {
                    if (!isDirty) {
                        onError("not_dirty");
                        return;
                    }
                    await handleSubmit(handleSave, () => {
                        onError("has_errors");
                    })();
                }
            }),
            [handleSubmit, handleSave, isDirty]
        );

        useEffect(() => {
            if (!isDirty && submitCount === 0) {
                const newDefaultValues = convertListingToDefaultValues(listing);
                reset(newDefaultValues);
            }
        }, [isDirty, listing, reset, submitCount]);

        useEffect(() => {
            const linkedAccountIDs = Object.values(listing.channels).reduce((acc, channel) => {
                if ("accountID" in channel) {
                    acc.push(channel.accountID);
                }
                return acc;
            }, [] as string[]);
            const linkedAccounts = accounts.filter(account =>
                linkedAccountIDs.includes(account._id)
            );
            setIsMessageOnlyScope(
                linkedAccounts.some(linkedAccount => linkedAccount.scope === "Messaging Only")
            );
        }, [accounts, listing]);

        useEffect(() => {
            if (submitCount > 0 && !isValid) {
                formContext.setErrors(errors);
            }
        }, [errors, formContext, isValid, submitCount]);

        return (
            <VStack space="lg">
                {!hidden && (
                    <>
                        <FormField
                            horizontal
                            label="Enable Listing"
                            tooltip="Disabling a listing will immediately stop all automation. You will also no longer be billed for the listing."
                        >
                            <Controller
                                control={control}
                                name="listingEnabled"
                                render={({field: {value}}) => (
                                    <Switch
                                        size="sm"
                                        testID="checkbox-enable-listing"
                                        value={value}
                                        onToggle={handleChangeListingEnabled}
                                    />
                                )}
                            />
                        </FormField>
                        <FormField
                            isInsideTab
                            label="Listing Nickname"
                            name="nickname"
                            tooltip={
                                <>
                                    Listing nickname is the listing name that we display in Host
                                    Tools. If you would like to edit the listing name that guests
                                    will see, you can{" "}
                                    <Pressable
                                        className="inline text-blue-600"
                                        onPress={onEditListingName}
                                    >
                                        edit it here
                                    </Pressable>
                                </>
                            }
                        >
                            <Controller
                                control={control}
                                name="nickname"
                                render={({field}) => {
                                    return (
                                        <Input>
                                            <InputField
                                                id="nickname"
                                                placeholder="Listing Nickname..."
                                                testID="input-nickname"
                                                type="text"
                                                value={field.value}
                                                onBlur={field.onBlur}
                                                onChange={field.onChange}
                                            />
                                        </Input>
                                    );
                                }}
                            />
                        </FormField>
                        <FormGroup label="Pricing">
                            <FormField
                                label="Price Source"
                                size="xs"
                                testID="price-source"
                                tooltip="The price source is what Host Tools will use as a base price when setting your prices. You can use a set amount, a supported dynamic pricing tool, or even another linked listing's prices (i.e., copy your prices from one linked listing to the other)."
                            >
                                {channels && (
                                    <Controller
                                        control={control}
                                        name="priceSource"
                                        render={({field: {value}}) => (
                                            <SelectPriceSource
                                                channels={channels}
                                                isDisabled={checkedMessageOnly}
                                                selectedValue={value}
                                                onSelectedOption={handleChangePriceSource}
                                            />
                                        )}
                                    />
                                )}
                            </FormField>
                            {priceSource !== "Beyond Pricing" && (
                                <HStack
                                    className="px-2 py-3 border border-solid border-blue-300 rounded-lg"
                                    space="lg"
                                >
                                    {priceSource === "Amount" && (
                                        <FormField
                                            error={errors.basePrice?.message}
                                            label="Base Price"
                                            name="basePrice"
                                            size="xs"
                                            tooltip="The starting price used for all dates"
                                        >
                                            <Controller
                                                control={control}
                                                name="basePrice"
                                                render={({field}) => {
                                                    return (
                                                        <Input isDisabled={checkedMessageOnly}>
                                                            <InputField
                                                                className="bg-white"
                                                                placeholder="Base Price"
                                                                testID="input-base-price"
                                                                type="number"
                                                                value={field.value || ""}
                                                                onBlur={field.onBlur}
                                                                onChange={field.onChange}
                                                            />
                                                        </Input>
                                                    );
                                                }}
                                                rules={{
                                                    validate: (basePrice, formData) => {
                                                        if (formData.pricingEnabled) {
                                                            if (priceSource === "Amount") {
                                                                if (
                                                                    Number.isNaN(
                                                                        Number(basePrice)
                                                                    ) ||
                                                                    Number(basePrice) <= 0
                                                                ) {
                                                                    return "Please add a base price that is greater than 0.";
                                                                }
                                                                // USD minimum price validation
                                                                if (
                                                                    listing.currency === "USD" &&
                                                                    Number(basePrice) < 15
                                                                ) {
                                                                    return "Base price must be at least $15 for USD currency.";
                                                                }
                                                                if (
                                                                    Number(basePrice) <
                                                                    Number(formData.minPrice)
                                                                ) {
                                                                    return "Please add a base price that is higher than your minimum price.";
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </FormField>
                                    )}
                                    <FormField
                                        error={errors.minPrice?.message}
                                        label="Minimum Price"
                                        name="minPrice"
                                        size="xs"
                                        tooltip="The lowest price Host Tools will allow your calendar to be set to. Any value below the minimum price will be set to the minimum price."
                                    >
                                        <Controller
                                            control={control}
                                            name="minPrice"
                                            render={({field}) => {
                                                return (
                                                    <Input isDisabled={checkedMessageOnly}>
                                                        <InputField
                                                            className="bg-white"
                                                            placeholder="Minimum Price"
                                                            testID="input-min-price"
                                                            type="number"
                                                            value={field.value || ""}
                                                            onBlur={field.onBlur}
                                                            onChange={field.onChange}
                                                        />
                                                    </Input>
                                                );
                                            }}
                                            rules={{
                                                validate: (minPrice, formData) => {
                                                    if (formData.pricingEnabled) {
                                                        if (
                                                            Number.isNaN(Number(minPrice)) ||
                                                            Number(minPrice) <= 0
                                                        ) {
                                                            return "Please add a minimum price that is greater than 0.";
                                                        }
                                                        // USD minimum price validation
                                                        if (
                                                            listing.currency === "USD" &&
                                                            Number(minPrice) < 15
                                                        ) {
                                                            return "Minimum price must be at least $15 for USD currency.";
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </FormField>
                                </HStack>
                            )}
                        </FormGroup>
                        <FormGroup label="Availability">
                            <HStack space="lg">
                                {priceSource !== "Beyond Pricing" &&
                                    priceSource !== "PriceLabs" && (
                                        <FormField
                                            className="w-1/2 flex-shrink"
                                            error={errors.minNights?.message}
                                            label="Minimum Nights"
                                            name="minNights"
                                            size="xs"
                                        >
                                            <Controller
                                                control={control}
                                                name="minNights"
                                                render={({field}) => {
                                                    return (
                                                        <Input isDisabled={checkedMessageOnly}>
                                                            <InputField
                                                                className="flex-1"
                                                                placeholder="Eg. (5, 10, 15)"
                                                                testID="input-min-nights"
                                                                type="number"
                                                                value={field.value || ""}
                                                                onBlur={field.onBlur}
                                                                onChange={field.onChange}
                                                            />
                                                            <InputRightAddon>
                                                                Nights
                                                            </InputRightAddon>
                                                        </Input>
                                                    );
                                                }}
                                                rules={{
                                                    validate: (minNights, listing) => {
                                                        if (listing.pricingEnabled) {
                                                            if (
                                                                Number.isNaN(Number(minNights)) ||
                                                                Number(minNights) <= 0
                                                            ) {
                                                                return "Please add a minimum nights that is greater than 0.";
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </FormField>
                                    )}
                                <FormField
                                    className="w-1/2 flex-shrink"
                                    error={errors.bookingWindow?.message}
                                    label="Availability Window"
                                    name="bookingWindow"
                                    size="xs"
                                >
                                    <Controller
                                        control={control}
                                        name="bookingWindow"
                                        render={({field: {value, onChange}}) => (
                                            <SelectGeneric
                                                options={bookingWindowOptions}
                                                placeholder="Booking Window..."
                                                testID="bookingWindow"
                                                value={`${value ?? ""}`}
                                                onChange={onChange}
                                            />
                                        )}
                                    />
                                </FormField>
                            </HStack>
                        </FormGroup>
                        {__IS_BETA__ && (
                            <FormGroup label="Discount">
                                <VStack space="sm">
                                    <Text variant="sm">Discount</Text>
                                    <HStack space="lg">
                                        <FormField
                                            className="flex-1"
                                            error={errors.weeklyDiscount?.message}
                                            label="Weekly Discount"
                                            size="xs"
                                        >
                                            <Input rightElement="%">
                                                <InputField
                                                    placeholder="Percent..."
                                                    testID="input-weekly-discount"
                                                    type="number"
                                                    {...register(
                                                        "weeklyDiscount",
                                                        discountNumberRules
                                                    )}
                                                />
                                            </Input>
                                        </FormField>
                                        <FormField
                                            className="flex-1"
                                            error={errors.monthlyDiscount?.message}
                                            label="Monthly Discount"
                                            size="xs"
                                        >
                                            <Input rightElement="%">
                                                <InputField
                                                    placeholder="Percent..."
                                                    testID="input-monthly-discount"
                                                    type="number"
                                                    {...register(
                                                        "monthlyDiscount",
                                                        discountNumberRules
                                                    )}
                                                />
                                            </Input>
                                        </FormField>
                                    </HStack>
                                </VStack>
                            </FormGroup>
                        )}
                        <FormGroup label="Additional Features">
                            <VStack space="lg">
                                <FormField
                                    label="Linked Rooms"
                                    rightElement={!isPro && <ProFeatureLocked />}
                                    size="xs"
                                    tooltip="This feature is useful if you have a property that can be rented as a whole unit or rented by the room. From the whole unit listing, select all the rooms you would like linked and when a reservation is added all the unit's availability will update"
                                >
                                    <SelectListings
                                        disabled={!isPro}
                                        field="linkedRoomID"
                                        listing={listing}
                                        listings={listings}
                                        onAdd={handleAddLinkedRoom}
                                        onChange={handleChangeLinkedRooms}
                                        onRemove={handleRemoveLinkedRoom}
                                    />
                                </FormField>
                                <FormField
                                    label="Calendar"
                                    size="xs"
                                    tooltip="You can import reservations from other channels we do not integrate with, or from your personal calendar. Import iCal links from channels we do not integrate with here."
                                >
                                    <Controller
                                        control={control}
                                        name="calendars"
                                        render={({field: {value, onChange}}) => (
                                            <ImportCalendars
                                                calendars={value}
                                                onChange={onChange}
                                            />
                                        )}
                                    />
                                </FormField>
                                <FormField
                                    label="Export Calendar"
                                    size="xs"
                                    tooltip="Export your listing's availability calendar."
                                >
                                    <HStack className="w-full items-center" space="sm">
                                        {calendarExportURL ? (
                                            <Link
                                                // min-w-0 limits the text length
                                                className="flex min-w-0 grow shrink"
                                                href={calendarExportURL}
                                                testID="link-url"
                                                type="external"
                                            >
                                                <Text isTruncated variant="sm">
                                                    {calendarExportURL}
                                                </Text>
                                            </Link>
                                        ) : (
                                            <Input isReadOnly className="grow">
                                                <InputField />
                                            </Input>
                                        )}
                                        <HStack className="shrink-0 grow-0 items-center" space="sm">
                                            <ButtonIconTooltip
                                                icon={RefreshIcon}
                                                testID="btn-refresh-icon"
                                                tooltip="Refresh"
                                                onPress={generateCalendarExportCode}
                                            />
                                            {calendarExportURL && (
                                                <CopyText value={calendarExportURL} />
                                            )}
                                        </HStack>
                                    </HStack>
                                </FormField>
                            </VStack>
                        </FormGroup>
                        <FormGroup label="Other Settings">
                            {canEditLock && (
                                <FormField
                                    label="Linked Locks"
                                    rightElement={!isPro && <ProFeatureLocked />}
                                    size="xs"
                                    tooltip="Link a smart lock to your listing and Host Tools will automatically create codes for each reservation that will only be active during each guest's stay. You can also include each guest's unique code as a message tag in your message rules."
                                >
                                    <SmartLock
                                        disabled={!isPro}
                                        listing={listing}
                                        locks={locks}
                                        onAdd={handleAddSmartLock}
                                        onChange={handleChangeLinkedLocks}
                                        onRemove={handleRemoveSmartLock}
                                    />
                                </FormField>
                            )}
                        </FormGroup>
                    </>
                )}
                <FormField
                    horizontal
                    label="Messaging Only"
                    tooltip="When enabling Messaging Only, Host Tools will no longer sync your calendar pricing or availability"
                >
                    <Controller
                        control={control}
                        name="checkedMessageOnly"
                        render={({field: {value}}) => (
                            <Switch
                                size="sm"
                                testID="checkbox-message-only"
                                value={value}
                                onToggle={handleToggleMessageOnly}
                            />
                        )}
                    />
                </FormField>
                {!listingEnabled && (
                    <FormField horizontal label="Hide Listing">
                        <Controller
                            control={control}
                            name="hidden"
                            render={({field: {value, onChange}}) => (
                                <Switch
                                    disabled={isMessageOnlyScope}
                                    size="sm"
                                    value={value}
                                    onToggle={onChange}
                                />
                            )}
                        />
                    </FormField>
                )}
            </VStack>
        );
    }
);

ListingSettings.displayName = "ListingSettings";

export default memo(ListingSettings);
